import { t } from "@airange/shared/i18n-admin-controls";
import { formatDateTimeLocalInputValue, parseDateTimeLocalInputValue } from "@airange/shared/date-time";
import { CalendarOutlined, SearchOutlined } from "@ant-design/icons";
import type { ProColumns } from "@ant-design/pro-components";
import { Button, Input, Space } from "antd";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { readTableFilterValue } from "./table-filter-values";

export { readTableFilterValue } from "./table-filter-values";

type AdminFilterDropdownRenderer = Extract<
  NonNullable<ProColumns<unknown>["filterDropdown"]>,
  (...args: never[]) => unknown
>;
type AdminFilterDropdownProps = Parameters<AdminFilterDropdownRenderer>[0];
type AdminControlledHeaderFilterResult = Pick<
  ProColumns<unknown>,
  "filterDropdown" | "filterIcon" | "filteredValue"
> & {
  draft: string;
  setDraft: Dispatch<SetStateAction<string>>;
};

type AdminHeaderFilterDropdownProps = {
  close: () => void;
  width: number;
  placeholder: string;
  draft: string;
  setDraft: (value: string) => void;
  apply: () => void;
  reset: () => void;
};

type UseControlledHeaderFilterOptions<TValue> = {
  value: TValue | undefined;
  placeholder: string;
  width?: number;
  toDraftValue?: (value: TValue | undefined) => string;
  parseDraft: (draft: string) => TValue | undefined;
  onApplyValue: (nextValue: TValue | undefined) => void;
};

type UseControlledTextHeaderFilterOptions = Omit<
  UseControlledHeaderFilterOptions<string>,
  "parseDraft"
>;
type UseControlledNumberHeaderFilterOptions = Omit<
  UseControlledHeaderFilterOptions<number>,
  "parseDraft"
>;
type AdminDateTimeRangeHeaderFilterOptions = {
  width?: number;
  fromPlaceholder?: string;
  toPlaceholder?: string;
};
export type AdminDateTimeRangeFilterValue = readonly [string | undefined, string | undefined];
export type AdminAuditFilterState = {
  created_by_keyword?: string;
  updated_by_keyword?: string;
  created_from?: string;
  created_to?: string;
  updated_from?: string;
  updated_to?: string;
};
type AdminDateTimeRangeFilterDropdownProps = Pick<
  AdminFilterDropdownProps,
  "selectedKeys" | "setSelectedKeys" | "confirm" | "clearFilters" | "close"
> & {
  width: number;
  fromPlaceholder?: string;
  toPlaceholder?: string;
};

function AdminHeaderFilterDropdown({
  close,
  width,
  placeholder,
  draft,
  setDraft,
  apply,
  reset,
}: AdminHeaderFilterDropdownProps) {
  return (
    <div style={{ padding: 8, width }}>
      <Input
        allowClear
        autoFocus
        placeholder={placeholder}
        value={draft}
        onChange={(event: { target: { value: string } }) => setDraft(event.target.value)}
        onPressEnter={() => {
          apply();
          close();
        }}
      />
      <Space style={{ marginTop: 8 }}>
        <Button
          size="small"
          color="primary"
          variant="solid"
          onClick={() => {
            apply();
            close();
          }}
        >
          {t("common.actions.search")}
        </Button>
        <Button
          size="small"
          onClick={() => {
            reset();
            close();
          }}
        >
          {t("common.actions.reset")}
        </Button>
      </Space>
    </div>
  );
}

function AdminDateTimeRangeFilterDropdown({
  selectedKeys,
  setSelectedKeys,
  confirm,
  clearFilters,
  close,
  width,
  fromPlaceholder,
  toPlaceholder,
}: AdminDateTimeRangeFilterDropdownProps) {
  const selectedFrom = typeof selectedKeys[0] === "string" ? selectedKeys[0] : "";
  const selectedTo = typeof selectedKeys[1] === "string" ? selectedKeys[1] : "";
  const [draftFrom, setDraftFrom] = useState(formatDateTimeLocalInputValue(selectedFrom) ?? "");
  const [draftTo, setDraftTo] = useState(formatDateTimeLocalInputValue(selectedTo) ?? "");

  useEffect(() => {
    setDraftFrom(formatDateTimeLocalInputValue(selectedFrom) ?? "");
    setDraftTo(formatDateTimeLocalInputValue(selectedTo) ?? "");
  }, [selectedFrom, selectedTo]);

  return (
    <div style={{ padding: 8, width }}>
      <Space orientation="vertical" size={8} style={{ width: "100%" }}>
        <Input
          allowClear
          type="datetime-local"
          placeholder={fromPlaceholder}
          value={draftFrom}
          onChange={(event: { target: { value: string } }) => {
            setDraftFrom(event.target.value);
          }}
        />
        <Input
          allowClear
          type="datetime-local"
          placeholder={toPlaceholder}
          value={draftTo}
          onChange={(event: { target: { value: string } }) => {
            setDraftTo(event.target.value);
          }}
        />
      </Space>
      <Space style={{ marginTop: 8 }}>
        <Button
          size="small"
          color="primary"
          variant="solid"
          onClick={() => {
            const nextFrom = parseDateTimeLocalInputValue(draftFrom) ?? "";
            const nextTo = parseDateTimeLocalInputValue(draftTo) ?? "";
            setSelectedKeys(nextFrom || nextTo ? [nextFrom, nextTo] : []);
            confirm();
            close();
          }}
        >
          {t("common.actions.search")}
        </Button>
        <Button
          size="small"
          onClick={() => {
            setDraftFrom("");
            setDraftTo("");
            clearFilters?.();
            confirm();
            close();
          }}
        >
          {t("common.actions.reset")}
        </Button>
      </Space>
    </div>
  );
}

function toOptionalTrimmedText(value: string) {
  const normalized = value.trim();
  return normalized || undefined;
}

function toOptionalFiniteNumber(value: string) {
  const normalized = value.trim();
  if (!normalized) return undefined;
  const numeric = Number(normalized);
  return Number.isFinite(numeric) ? numeric : undefined;
}

export function useControlledHeaderFilter<TValue>({
  value,
  placeholder,
  width = 240,
  toDraftValue,
  parseDraft,
  onApplyValue,
}: UseControlledHeaderFilterOptions<TValue>): AdminControlledHeaderFilterResult {
  const formatDraftValue = useCallback(
    (current: TValue | undefined) => {
      if (toDraftValue) {
        return toDraftValue(current);
      }
      return current === undefined ? "" : String(current);
    },
    [toDraftValue],
  );

  const activeDraftValue = formatDraftValue(value);
  const [draft, setDraft] = useState(activeDraftValue);

  useEffect(() => {
    setDraft(activeDraftValue);
  }, [activeDraftValue]);

  const apply = useCallback(() => {
    onApplyValue(parseDraft(draft));
  }, [draft, onApplyValue, parseDraft]);

  const reset = useCallback(() => {
    setDraft("");
    onApplyValue(undefined);
  }, [onApplyValue]);

  const active = activeDraftValue.trim().length > 0;

  const filterIcon = useMemo(
    () => (
      <span style={{ display: "inline-flex" }}>
        <SearchOutlined style={{ color: active ? "#1677ff" : undefined }} />
      </span>
    ),
    [active],
  );

  const filterDropdown = useCallback(
    ({ close }: Pick<AdminFilterDropdownProps, "close">) => (
      <AdminHeaderFilterDropdown
        close={close}
        width={width}
        placeholder={placeholder}
        draft={draft}
        setDraft={setDraft}
        apply={apply}
        reset={reset}
      />
    ),
    [apply, draft, placeholder, reset, width],
  );

  return useMemo(
    () => ({
      draft,
      setDraft,
      filterIcon,
      filterDropdown,
      filteredValue: active ? [activeDraftValue] : null,
    }),
    [active, activeDraftValue, draft, filterDropdown, filterIcon],
  );
}

export function useControlledTextHeaderFilter(
  options: UseControlledTextHeaderFilterOptions,
): AdminControlledHeaderFilterResult {
  return useControlledHeaderFilter<string>({
    ...options,
    parseDraft: toOptionalTrimmedText,
  });
}

export function useControlledNumberHeaderFilter(
  options: UseControlledNumberHeaderFilterOptions,
): AdminControlledHeaderFilterResult {
  return useControlledHeaderFilter<number>({
    ...options,
    parseDraft: toOptionalFiniteNumber,
  });
}

export function buildTextHeaderFilter(
  placeholder = "Search",
): Pick<ProColumns<unknown>, "filterIcon" | "filterDropdown"> {
  return {
    filterIcon: <SearchOutlined />,
    filterDropdown: (props: AdminFilterDropdownProps) => {
      const { selectedKeys, setSelectedKeys, confirm, clearFilters, close } = props;
      return (
        <div style={{ padding: 8, width: 260 }}>
          <Input
            allowClear
            autoFocus
            placeholder={placeholder}
            value={String(selectedKeys[0] ?? "")}
            onChange={(event: { target: { value: string } }) => {
              const nextValue = event.target.value;
              setSelectedKeys(nextValue ? [nextValue] : []);
            }}
            onPressEnter={() => {
              confirm();
              close();
            }}
          />
          <Space style={{ marginTop: 8 }}>
            <Button
              size="small"
              color="primary"
              variant="solid"
              onClick={() => {
                confirm();
                close();
              }}
            >
              {t("common.actions.search")}
            </Button>
            <Button
              size="small"
              onClick={() => {
                clearFilters?.();
                confirm();
                close();
              }}
            >
              {t("common.actions.reset")}
            </Button>
          </Space>
        </div>
      );
    },
  };
}

export function buildDateTimeRangeHeaderFilter(
  options: AdminDateTimeRangeHeaderFilterOptions = {},
): Pick<ProColumns<unknown>, "filterIcon" | "filterDropdown"> {
  const { width = 320, fromPlaceholder, toPlaceholder } = options;
  return {
    filterIcon: (filtered: boolean) => (
      <CalendarOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    filterDropdown: (props: AdminFilterDropdownProps) => {
      return (
        <AdminDateTimeRangeFilterDropdown
          {...props}
          width={width}
          {...(fromPlaceholder === undefined ? {} : { fromPlaceholder })}
          {...(toPlaceholder === undefined ? {} : { toPlaceholder })}
        />
      );
    },
  };
}

export function readTableDateTimeRangeFilterValue(
  filters: Record<string, unknown> | null | undefined,
  key: string,
): AdminDateTimeRangeFilterValue | undefined {
  const raw = filters?.[key];
  if (!raw || !Array.isArray(raw) || raw.length === 0) return undefined;
  const from = raw[0] == null ? undefined : String(raw[0]).trim() || undefined;
  const to = raw[1] == null ? undefined : String(raw[1]).trim() || undefined;
  if (!from && !to) return undefined;
  return [from, to] as const;
}

export function buildAuditActorHeaderFilteredValue(value: string | null | undefined) {
  const normalized = typeof value === "string" ? value.trim() : "";
  return normalized ? [normalized] : null;
}

export function buildAuditDateTimeRangeHeaderFilteredValue(
  from: string | null | undefined,
  to: string | null | undefined,
) {
  const normalizedFrom = typeof from === "string" ? from.trim() : "";
  const normalizedTo = typeof to === "string" ? to.trim() : "";
  return normalizedFrom || normalizedTo ? [normalizedFrom, normalizedTo] : null;
}

export function readTableAuditFilterValues(
  filters: Record<string, unknown> | null | undefined,
): AdminAuditFilterState {
  const createdAtRange = readTableDateTimeRangeFilterValue(filters, "created_at");
  const updatedAtRange = readTableDateTimeRangeFilterValue(filters, "updated_at");
  const values: AdminAuditFilterState = {};
  const createdBy = readTableFilterValue(filters, "created_by");
  const updatedBy = readTableFilterValue(filters, "updated_by");
  if (createdBy !== undefined) values.created_by_keyword = createdBy;
  if (updatedBy !== undefined) values.updated_by_keyword = updatedBy;
  if (createdAtRange?.[0] !== undefined) values.created_from = createdAtRange[0];
  if (createdAtRange?.[1] !== undefined) values.created_to = createdAtRange[1];
  if (updatedAtRange?.[0] !== undefined) values.updated_from = updatedAtRange[0];
  if (updatedAtRange?.[1] !== undefined) values.updated_to = updatedAtRange[1];
  return values;
}
